import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useSites } from "@/contexts/SiteContext";
import PageHeader from "@/components/PageHeader";
import { toast } from "sonner";
import {
  Printer, Settings2, Loader2, TrendingUp, TrendingDown, Minus,
  ChevronDown, ChevronUp, CheckCircle2, AlertTriangle, FileText, Target,
} from "lucide-react";

const MONTHS_FR = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet",
  "août", "septembre", "octobre", "novembre", "décembre"];

const lastMonths = (n = 12) => {
  const out = [];
  const now = new Date();
  for (let i = 1; i <= n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: `${MONTHS_FR[d.getMonth()]} ${d.getFullYear()}`,
    });
  }
  return out;
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-CH") : "—");

const Delta = ({ value, invert = false, suffix = "" }) => {
  if (value === null || value === undefined) return <span className="text-slate-400 text-xs">—</span>;
  const good = invert ? value < 0 : value > 0;
  const Icon = value === 0 ? Minus : good ? TrendingUp : TrendingDown;
  const color = value === 0 ? "text-slate-400" : good ? "text-emerald-600" : "text-red-600";
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium ${color}`}>
      <Icon className="w-3.5 h-3.5" />
      {value > 0 ? "+" : ""}{value}{suffix}
    </span>
  );
};

const Section = ({ title, testid, children }) => (
  <section className="bg-white border border-slate-200 rounded-md p-5 mb-4 print:break-inside-avoid" data-testid={testid}>
    <h2 className="text-sm font-display font-semibold text-slate-950 uppercase tracking-wide mb-3">{title}</h2>
    {children}
  </section>
);

export default function Reports() {
  const { activeSite } = useSites();
  const months = lastMonths();
  const [month, setMonth] = useState(months[0].value);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [brandingOpen, setBrandingOpen] = useState(false);
  const [brandingForm, setBrandingForm] = useState({ company_name: "", logo_url: "" });
  const [showTech, setShowTech] = useState(false);

  const load = useCallback(async () => {
    if (!activeSite) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/sites/${activeSite.id}/monthly-report?month=${month}`);
      setData(data);
      setBrandingForm({
        company_name: data.branding?.company_name || "",
        logo_url: data.branding?.logo_url || "",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur de chargement du rapport");
    } finally {
      setLoading(false);
    }
  }, [activeSite, month]);

  useEffect(() => { load(); }, [load]);

  const saveBranding = async (e) => {
    e.preventDefault();
    try {
      await api.patch("/report-branding", brandingForm);
      toast.success("En-tête du rapport enregistré ✓");
      setBrandingOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur d'enregistrement");
    }
  };

  if (!activeSite) return null;
  const s = data?.scores;
  const gsc = data?.gsc;

  return (
    <div className="p-6 md:p-8 max-w-5xl print:p-0 print:max-w-none" data-testid="reports-page">
      <div className="print:hidden">
        <PageHeader
          overline={`Rapport mensuel · ${activeSite.label}`}
          title="Rapport client mensuel"
          description="Où on en est, ce qui a été amélioré, quel résultat, et ce qu'on fait ensuite."
          action={
            <div className="flex items-center gap-2">
              <select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                data-testid="report-month-select"
                className="border border-slate-300 rounded-md px-3 py-2 text-sm bg-white"
              >
                {months.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
              <button
                onClick={() => setBrandingOpen((v) => !v)}
                data-testid="report-branding-btn"
                className="inline-flex items-center gap-2 bg-white border border-slate-300 hover:border-[#002FA7] hover:text-[#002FA7] text-slate-700 px-3 py-2 rounded-md text-sm font-medium transition-colors"
              >
                <Settings2 className="w-4 h-4" /> En-tête
              </button>
              <button
                onClick={() => window.print()}
                data-testid="report-print-btn"
                className="inline-flex items-center gap-2 bg-[#002FA7] hover:bg-[#001D6B] text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                <Printer className="w-4 h-4" /> Exporter PDF
              </button>
            </div>
          }
        />
        {brandingOpen && (
          <form onSubmit={saveBranding} className="mb-5 p-4 border border-slate-200 bg-white rounded-md grid sm:grid-cols-3 gap-3 items-end" data-testid="report-branding-form">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Nom de votre entreprise</label>
              <input value={brandingForm.company_name}
                onChange={(e) => setBrandingForm((f) => ({ ...f, company_name: e.target.value }))}
                data-testid="report-branding-company" placeholder="Ex : Janou Info Design"
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">URL du logo (optionnel)</label>
              <input value={brandingForm.logo_url}
                onChange={(e) => setBrandingForm((f) => ({ ...f, logo_url: e.target.value }))}
                data-testid="report-branding-logo" placeholder="https://…/logo.png"
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
            </div>
            <button type="submit" data-testid="report-branding-save"
              className="bg-[#002FA7] hover:bg-[#001D6B] text-white px-4 py-2 rounded-md text-sm font-medium">
              Enregistrer
            </button>
          </form>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500 text-sm py-16 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Génération du rapport…
        </div>
      ) : !data ? null : (
        <div data-testid="report-content">
          {/* En-tête marque blanche */}
          <div className="flex items-center justify-between border-b-2 border-[#002FA7] pb-4 mb-5" data-testid="report-header">
            <div className="flex items-center gap-3 min-w-0">
              {data.branding?.logo_url && (
                <img src={data.branding.logo_url} alt="" className="h-10 w-auto max-w-[140px] object-contain" />
              )}
              <div className="min-w-0">
                <div className="font-display font-semibold text-slate-950 truncate">
                  {data.branding?.company_name || "Rapport SEO"}
                </div>
                <div className="text-xs text-slate-500">Rapport SEO mensuel — {data.month_label}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="font-medium text-sm text-slate-900">{data.site.label}</div>
              <div className="text-xs text-slate-500">{data.site.base_url}</div>
            </div>
          </div>

          {/* Scores */}
          <Section title="Score SEO" testid="report-scores">
            {!s ? (
              <p className="text-sm text-slate-500">Aucun audit disponible pour ce site.</p>
            ) : (
              <>
                <div className="flex items-end gap-6 flex-wrap mb-4">
                  <div>
                    <div className="text-4xl font-display font-bold text-slate-950" data-testid="report-global-score">{s.current ?? "—"}<span className="text-lg text-slate-400">/100</span></div>
                    <div className="text-xs text-slate-500 mt-1">
                      Audit du {fmtDate(s.audit_date)}{!s.audit_in_month && " (dernier disponible)"}
                    </div>
                  </div>
                  <div className="pb-1">
                    <Delta value={s.delta} suffix=" pt" />
                    {s.previous !== null && s.previous !== undefined && (
                      <div className="text-[11px] text-slate-400">vs {s.previous}/100 ({fmtDate(s.previous_audit_date)})</div>
                    )}
                  </div>
                </div>
                {s.by_language?.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="report-lang-scores">
                    {s.by_language.map((l) => (
                      <div key={l.lang} className="border border-slate-200 rounded-md p-3">
                        <div className="text-[11px] font-semibold text-slate-500 uppercase">{l.lang}</div>
                        <div className="text-xl font-display font-bold text-slate-950">{l.score ?? "—"}</div>
                        <Delta value={l.delta} suffix=" pt" />
                        <div className="text-[10px] text-slate-400">{l.pages} page(s)</div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </Section>

          {/* Corrections */}
          <Section title="Corrections SEO" testid="report-fixes">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
              <div className="border border-slate-200 rounded-md p-3">
                <div className="text-xl font-display font-bold text-slate-950">{data.fixes.applied}</div>
                <div className="text-[11px] text-slate-500">appliquées ce mois</div>
              </div>
              <div className="border border-slate-200 rounded-md p-3">
                <div className="text-xl font-display font-bold text-emerald-600">{data.resolution.verified_fixed}</div>
                <div className="text-[11px] text-slate-500">vérifiées corrigées</div>
              </div>
              <div className="border border-slate-200 rounded-md p-3">
                <div className="text-xl font-display font-bold text-amber-600">{data.resolution.pending_verification}</div>
                <div className="text-[11px] text-slate-500">à vérifier</div>
              </div>
              <div className="border border-slate-200 rounded-md p-3">
                <div className={`text-xl font-display font-bold ${data.resolution.regressions ? "text-red-600" : "text-slate-950"}`}>{data.resolution.regressions}</div>
                <div className="text-[11px] text-slate-500">régression(s)</div>
              </div>
            </div>
            {data.resolution.regression_items?.length > 0 && (
              <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md p-3" data-testid="report-regressions">
                <div className="font-medium mb-1 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Régressions à réappliquer</div>
                {data.resolution.regression_items.map((r, i) => (
                  <div key={i} className="truncate">{r.category} — {r.page_url}</div>
                ))}
              </div>
            )}
          </Section>

          {/* Articles publiés */}
          <Section title={`Articles publiés (${data.articles.length})`} testid="report-articles">
            {data.articles.length === 0 ? (
              <p className="text-sm text-slate-500">Aucun article publié ce mois-ci.</p>
            ) : (
              <ul className="space-y-1.5">
                {data.articles.map((a, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                    <FileText className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                    <span className="truncate">{a.title}</span>
                    <span className="text-[10px] text-slate-400 flex-shrink-0">{a.type} · {fmtDate(a.published_at)}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/* Search Console */}
          <Section title="Google Search Console" testid="report-gsc">
            {gsc?.status !== "OK" ? (
              <p className="text-sm text-slate-500">Search Console non synchronisée pour ce site.</p>
            ) : (
              <>
                <div className="text-[11px] text-slate-400 mb-3">
                  Période synchronisée : {gsc.period?.start} → {gsc.period?.end} (vs période précédente équivalente)
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {[["Clics", gsc.totals?.clicks, gsc.deltas?.clicks, false],
                    ["Impressions", gsc.totals?.impressions, gsc.deltas?.impressions, false],
                    ["Position moy.", gsc.totals?.position, gsc.deltas?.position, true]].map(([label, val, delta, inv]) => (
                    <div key={label} className="border border-slate-200 rounded-md p-3">
                      <div className="text-[11px] text-slate-500">{label}</div>
                      <div className="text-xl font-display font-bold text-slate-950">{val ?? "—"}</div>
                      <Delta value={delta} invert={inv} />
                    </div>
                  ))}
                </div>
                {gsc.quick_wins?.length > 0 && (
                  <div data-testid="report-quickwins">
                    <div className="text-xs font-semibold text-slate-700 mb-1.5">Quick wins (position 4-20, portée de la 1ʳᵉ page)</div>
                    <ul className="space-y-1">
                      {gsc.quick_wins.map((q, i) => (
                        <li key={i} className="text-sm text-slate-700 flex items-center gap-2">
                          <Target className="w-3.5 h-3.5 text-[#002FA7] flex-shrink-0" />
                          <span className="truncate">« {q.query} »</span>
                          <span className="text-[10px] text-slate-400 flex-shrink-0">pos. {q.position} · {q.impressions} impr.</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </Section>

          {/* Impact business */}
          {data.business?.measurable && (
            <Section title="Impact business" testid="report-business">
              <div className="text-sm text-slate-700 mb-2">
                Trafic organique : <strong>{data.business.organic_clicks} clics</strong>{" "}
                <Delta value={data.business.organic_clicks_delta} /> vs période précédente.
              </div>
              {data.business.top_progress_pages?.length > 0 && (
                <>
                  <div className="text-xs font-semibold text-slate-700 mb-1.5">Pages en plus forte progression</div>
                  <ul className="space-y-1">
                    {data.business.top_progress_pages.map((p, i) => (
                      <li key={i} className="text-sm text-slate-700 flex items-center gap-2">
                        <TrendingUp className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                        <span className="truncate">{p.page}</span>
                        <span className="text-[10px] text-slate-400 flex-shrink-0">+{p.delta_clicks} clics</span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </Section>
          )}

          {/* Priorités mois suivant */}
          <Section title="Top 3 priorités du mois suivant" testid="report-priorities">
            {data.priorities.length === 0 ? (
              <p className="text-sm text-slate-500">Rien de bloquant — poursuite de la stratégie de contenu.</p>
            ) : (
              <ol className="space-y-2">
                {data.priorities.map((p, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-sm text-slate-700">
                    <span className="w-5 h-5 rounded-full bg-[#002FA7] text-white text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                    {p}
                  </li>
                ))}
              </ol>
            )}
          </Section>

          {/* Annexe technique repliable */}
          {s?.categories?.length > 0 && (
            <div className="border border-slate-200 rounded-md bg-white mb-4">
              <button
                onClick={() => setShowTech((v) => !v)}
                data-testid="report-technical-toggle"
                className="w-full flex items-center justify-between px-5 py-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Détails techniques (annexe)
                {showTech ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
              {showTech && (
                <div className="px-5 pb-4" data-testid="report-technical">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {s.categories.map((c) => (
                      <div key={c.key} className="border border-slate-100 rounded-md p-3">
                        <div className="text-[11px] text-slate-500">{c.label}</div>
                        <div className="text-lg font-display font-bold text-slate-950">{c.score ?? "—"}</div>
                        <div className="text-[10px] text-slate-400">{c.issues_total} problème(s)</div>
                      </div>
                    ))}
                  </div>
                  <div className="text-[11px] text-slate-400 mt-2">
                    {s.evaluated_urls} URL(s) auditée(s) · priorités : {Object.entries(s.priorities_total || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="text-[10px] text-slate-400 text-center pb-6 flex items-center justify-center gap-1.5">
            <CheckCircle2 className="w-3 h-3" />
            Rapport généré le {fmtDate(data.generated_at)} — LOGI SEO Booster
          </div>
        </div>
      )}
    </div>
  );
}
